/* =====================================================================
 * EditorToolbar — 문서 에디터 서식 도구모음
 * ===================================================================== */

import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import type { Editor } from '@tiptap/react';
import { scheduleForceFixTables } from './tableFix';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code,
  Minus,
  Image as ImageIcon,
  Table as TableIcon,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Heading1,
  Heading2,
  Heading3,
  Type,
  Palette,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Props                                                               */
/* ------------------------------------------------------------------ */

interface EditorToolbarProps {
  editor: Editor;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── 이미지 삽입 (빈 imageBlock 삽입 → placeholder에서 업로드) ── */
  const handleImageUpload = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>) => {
      editor.chain().focus().insertContent({ type: 'imageBlock' }).run();
      _e.target.value = '';
    },
    [editor],
  );

  /* ── 링크 삽입 ── */
  const handleLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL 입력:', previousUrl);

    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  }, [editor]);

  /* ── 표 삽입 ── */
  const handleTable = useCallback(() => {
    if (import.meta.env.DEV) {
      const w = window as Window & { __TABLE_DEBUG_LOGS__?: Array<Record<string, unknown>> };
      const entry = {
        at: new Date().toISOString(),
        label: 'insert-table:EditorToolbar',
        payload: {
          selectionFrom: editor.state.selection.from,
          selectionTo: editor.state.selection.to,
        },
      };
      if (!w.__TABLE_DEBUG_LOGS__) w.__TABLE_DEBUG_LOGS__ = [];
      w.__TABLE_DEBUG_LOGS__.push(entry);
      // eslint-disable-next-line no-console
      console.log('[TableDebug] insert-table:EditorToolbar', entry.payload);
    }
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
    scheduleForceFixTables(editor);
  }, [editor]);

  /* ── 텍스트 색상 ── */
  const colors = [
    '#000000', '#434343', '#666666', '#999999',
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6',
  ];

  return (
    <ToolbarWrap>
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="실행 취소"
        >
          <Undo2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="다시 실행"
        >
          <Redo2 size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          $active={editor.isActive('heading', { level: 1 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          title="제목 1"
        >
          <Heading1 size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('heading', { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          title="제목 2"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('heading', { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          title="제목 3"
        >
          <Heading3 size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
          title="본문"
        >
          <Type size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          $active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="굵게 (Ctrl+B)"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="기울임 (Ctrl+I)"
        >
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="밑줄 (Ctrl+U)"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="취소선"
        >
          <Strikethrough size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('highlight')}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          title="형광펜"
        >
          <Highlighter size={16} />
        </ToolbarButton>
        <ColorPickerWrap>
          <ToolbarButton
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="글자 색상"
          >
            <Palette size={16} />
          </ToolbarButton>
          {showColorPicker && (
            <ColorDropdown>
              {colors.map((color) => (
                <ColorSwatch
                  key={color}
                  $color={color}
                  onClick={() => {
                    editor.chain().focus().setColor(color).run();
                    setShowColorPicker(false);
                  }}
                />
              ))}
              <ColorSwatch
                $color="transparent"
                $isReset
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
                title="색상 초기화"
              />
            </ColorDropdown>
          )}
        </ColorPickerWrap>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          $active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="왼쪽 정렬"
        >
          <AlignLeft size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="가운데 정렬"
        >
          <AlignCenter size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="오른쪽 정렬"
        >
          <AlignRight size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          $active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="글머리 기호 목록"
        >
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="번호 목록"
        >
          <ListOrdered size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('taskList')}
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          title="할 일 목록"
        >
          <CheckSquare size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          $active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="인용"
        >
          <Quote size={16} />
        </ToolbarButton>
        <ToolbarButton
          $active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          title="코드 블록"
        >
          <Code size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="구분선"
        >
          <Minus size={16} />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton onClick={handleLink} title="링크 삽입">
          <LinkIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => fileInputRef.current?.click()}
          title="이미지 삽입"
        >
          <ImageIcon size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={handleTable} title="표 삽입">
          <TableIcon size={16} />
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </ToolbarGroup>
    </ToolbarWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const ToolbarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  overflow-x: auto;
  flex-shrink: 0;

  &::-webkit-scrollbar {
    height: 0;
  }
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 4px;
  flex-shrink: 0;
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(0,0,0,0.08)'
      : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  cursor: pointer;
  transition: all 150ms;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ColorPickerWrap = styled.div`
  position: relative;
`;

const ColorDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 50;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  margin-top: 4px;
`;

const ColorSwatch = styled.button<{ $color: string; $isReset?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  cursor: pointer;
  position: relative;
  transition: transform 150ms;

  &:hover {
    transform: scale(1.15);
  }

  ${({ $isReset }) =>
    $isReset &&
    `
    &::after {
      content: '✕';
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      color: #999;
    }
  `}
`;
